import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Users,
  Crown,
  Sparkles,
  UserPlus,
  TrendingUp,
  TrendingDown,
  Handshake,
  RefreshCw,
  Clock,
  MessageSquare,
  Shield,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Globe,
  Star,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import CompanyAdminLayout from '../../components/CompanyAdminLayout';
import { companyAdminCustomersAPI } from '../../lib/companyAdminApi';
import { useCompanyAdminAuth } from '../../contexts/CompanyAdminAuthContext';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-';

const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

// ─── KPI Card ───────────────────────────────────────────────────────────────
const KpiCard = ({ title, value, icon: Icon, color, subtext, change, testid }) => {
  const isPositive = typeof change === 'number' ? change >= 0 : null;
  return (
    <div
      data-testid={testid}
      className="bg-card rounded-xl border border-border p-5 flex flex-col gap-3 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <div className={cn('p-2.5 rounded-lg', color)}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      <div>
        <p className="text-3xl font-bold text-foreground">
          {typeof value === 'number' ? value.toLocaleString() : value ?? '—'}
        </p>
        {(subtext || change != null) && (
          <div className="flex items-center gap-2 mt-1">
            {change != null && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 text-xs font-semibold',
                  isPositive ? 'text-green-600' : 'text-red-500'
                )}
              >
                {isPositive ? (
                  <ArrowUpRight className="w-3 h-3" />
                ) : (
                  <ArrowDownRight className="w-3 h-3" />
                )}
                {Math.abs(change)}%
              </span>
            )}
            {subtext && <span className="text-xs text-muted-foreground">{subtext}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Section Header ──────────────────────────────────────────────────────────
const SectionHeader = ({ title, subtitle }) => (
  <div className="mb-4">
    <h2 className="text-base font-semibold text-foreground">{title}</h2>
    {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
  </div>
);

// ─── Subscription badge ──────────────────────────────────────────────────────
const SubBadge = ({ premium, ai }) => {
  if (premium && ai)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700">
        <Star className="w-3 h-3" />
        Both
      </span>
    );
  if (premium)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
        <Crown className="w-3 h-3" />
        Premium
      </span>
    );
  if (ai)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        <Sparkles className="w-3 h-3" />
        AI Plan
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-gray-600">
      Free
    </span>
  );
};

// ─── Pie chart colours ───────────────────────────────────────────────────────
const PIE_COLORS = ['#6366f1', '#3b82f6', '#22c55e', '#94a3b8'];

// ─── Custom Tooltip for Recharts ─────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }} className="font-medium">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

// ─── Main Dashboard ──────────────────────────────────────────────────────────
const CompanyAdminDashboardPage = () => {
  const { company } = useCompanyAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.getDashboard();
      setData(res.data);
      setLastUpdated(new Date());
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <CompanyAdminLayout>
        <div
          data-testid="dashboard-loading"
          className="flex items-center justify-center h-64 text-muted-foreground"
        >
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Loading dashboard…
        </div>
      </CompanyAdminLayout>
    );
  }

  const ov = data?.overview || {};
  const signups = data?.signups || {};
  const trend = data?.seven_day_trend || [];
  const subBreakdown = data?.subscription_breakdown || {};
  const partners = data?.partners || {};
  const growth = data?.monthly_growth?.customers || {};
  const recentPayments = data?.recent_payment_history || [];
  const topCustomers = data?.top_active_customers || [];
  const recentSignups = data?.recent_customer_signups || [];
  const chats = data?.chats || {};
  const countryData = (data?.timezone_distribution || []).slice(0, 10);
  const dietPlanTrend = data?.diet_plan_trend || [];

  // Pie data for subscription breakdown
  const pieData = [
    { name: 'Premium Only', value: subBreakdown.premium_only || 0 },
    { name: 'AI Plan Only', value: subBreakdown.ai_plan_only || 0 },
    { name: 'Both', value: subBreakdown.both || 0 },
    { name: 'Free', value: subBreakdown.free || 0 },
  ].filter((d) => d.value > 0);

  return (
    <CompanyAdminLayout>
      <div data-testid="company-admin-dashboard" className="space-y-6 pb-6">
        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Welcome back, {company?.name || company?.email}
              {lastUpdated && (
                <span className="ml-2 text-xs">
                  · Updated {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="dashboard-refresh-btn"
          >
            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>

        {/* ── KPI Row ── */}
        <div
          data-testid="dashboard-kpi-cards"
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <KpiCard
            testid="kpi-total-customers"
            title="Total Customers"
            value={ov.total_customers}
            icon={Users}
            color="bg-slate-500"
            subtext={`${signups.today} today`}
          />
          <KpiCard
            testid="kpi-paid-customers"
            title="Paid Customers"
            value={ov.total_paid_customers}
            icon={Crown}
            color="bg-purple-500"
            subtext={`${ov.expiring_soon || 0} expiring soon`}
          />
          <KpiCard
            testid="kpi-active-partners"
            title="Active Partners"
            value={ov.active_partners}
            icon={Handshake}
            color="bg-emerald-500"
            subtext={`${ov.total_partners} total`}
          />
          <KpiCard
            testid="kpi-new-signups"
            title="New Signups (7d)"
            value={signups.this_week}
            icon={UserPlus}
            color="bg-blue-500"
            change={growth.change != null ? Number(growth.change) : null}
            subtext="vs last month"
          />
        </div>

        {/* ── Secondary KPI Row ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard
            testid="kpi-active-premium"
            title="Active Premium"
            value={ov.active_premium}
            icon={Crown}
            color="bg-violet-500"
          />
          <KpiCard
            testid="kpi-active-ai"
            title="Active AI Plan"
            value={ov.active_ai_plan}
            icon={Sparkles}
            color="bg-sky-500"
          />
          <KpiCard
            testid="kpi-free-users"
            title="Free Users"
            value={ov.free_users}
            icon={Shield}
            color="bg-gray-400"
          />
          <KpiCard
            testid="kpi-chat-rooms"
            title="Active Chats (7d)"
            value={chats.active_rooms}
            icon={MessageSquare}
            color="bg-orange-500"
            subtext={`${chats.total_rooms} total rooms`}
          />
        </div>

        {/* ── 7-Day Trend Charts ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Signups + Payments Line */}
          <div
            data-testid="chart-7day-trend"
            className="bg-card rounded-xl border border-border p-5 shadow-sm"
          >
            <SectionHeader
              title="7-Day Activity Trend"
              subtitle="Daily new signups and payment events"
            />
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} />
                <Line
                  type="monotone"
                  dataKey="signups"
                  name="Signups"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="payments"
                  name="Payments"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Bar chart */}
          <div
            data-testid="chart-7day-bar"
            className="bg-card rounded-xl border border-border p-5 shadow-sm"
          >
            <SectionHeader
              title="7-Day Signups vs Payments"
              subtitle="Grouped bar comparison"
            />
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} />
                <Bar dataKey="signups" name="Signups" fill="#6366f1" radius={[3, 3, 0, 0]} />
                <Bar dataKey="payments" name="Payments" fill="#22c55e" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Diet Plan Weekly Graph ── */}
        <div
          data-testid="chart-diet-plan-weekly"
          className="bg-card rounded-xl border border-border p-5 shadow-sm"
        >
          <SectionHeader
            title="Weekly Diet Plan Activity"
            subtitle="Customers who received a diet plan each day (last 7 days)"
          />
          {dietPlanTrend.length > 0 && dietPlanTrend.some(d => d.total > 0) ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={dietPlanTrend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} />
                <Bar dataKey="strict" name="Strict Diet Plan" fill="#6366f1" radius={[3, 3, 0, 0]} stackId="a" />
                <Bar dataKey="moderate" name="Moderate Diet Plan" fill="#22c55e" radius={[3, 3, 0, 0]} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-40 gap-2 text-muted-foreground">
              <Activity className="w-8 h-8 opacity-30" />
              <p className="text-sm">No diet plans recorded in the last 7 days</p>
            </div>
          )}
          {/* Summary row */}
          {dietPlanTrend.length > 0 && (
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-border">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Total (7 days)</p>
                <p className="text-xl font-bold text-foreground">
                  {dietPlanTrend.reduce((s, d) => s + d.total, 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Strict Plans</p>
                <p className="text-xl font-bold text-indigo-600">
                  {dietPlanTrend.reduce((s, d) => s + d.strict, 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Moderate Plans</p>
                <p className="text-xl font-bold text-emerald-600">
                  {dietPlanTrend.reduce((s, d) => s + d.moderate, 0)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ── Subscription Breakdown + Monthly Growth ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pie */}
          <div
            data-testid="chart-subscription-pie"
            className="bg-card rounded-xl border border-border p-5 shadow-sm"
          >
            <SectionHeader title="Subscription Breakdown" />
            {pieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 space-y-1">
                  {pieData.map((d, i) => (
                    <div key={d.name} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                        />
                        <span className="text-muted-foreground">{d.name}</span>
                      </div>
                      <span className="font-semibold">{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-10">
                No subscription data
              </p>
            )}
          </div>

          {/* Monthly growth + Signups summary */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Monthly growth */}
            <div className="bg-card rounded-xl border border-border p-5 shadow-sm">
              <SectionHeader title="Monthly Growth" subtitle="Customer acquisition" />
              <div className="space-y-4 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">This Month</span>
                  <span className="text-xl font-bold text-foreground">
                    {growth.current ?? '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Last Month</span>
                  <span className="text-xl font-bold text-foreground">
                    {growth.previous ?? '—'}
                  </span>
                </div>
                <div className="pt-2 border-t border-border flex justify-between items-center">
                  <span className="text-sm font-medium">Change</span>
                  {growth.change != null ? (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 text-sm font-bold',
                        Number(growth.change) >= 0 ? 'text-green-600' : 'text-red-500'
                      )}
                    >
                      {Number(growth.change) >= 0 ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                      {growth.change}%
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </div>
              </div>
            </div>

            {/* Signup summary */}
            <div className="bg-card rounded-xl border border-border p-5 shadow-sm">
              <SectionHeader title="Signup Summary" />
              <div className="space-y-3 mt-2">
                {[
                  { label: 'Today', value: signups.today },
                  { label: 'This Week', value: signups.this_week },
                  { label: 'This Month', value: signups.this_month },
                  { label: 'Last Month', value: signups.last_month },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{label}</span>
                    <span className="font-bold text-foreground">{value ?? '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Top Active Customers ── */}
        <div
          data-testid="table-top-customers"
          className="bg-card rounded-xl border border-border shadow-sm overflow-hidden"
        >
          <div className="p-5 border-b border-border flex items-center gap-2">
            <Activity className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-base font-semibold">Top Active Paid Customers</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Customer
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Plan
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Expiry
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      No active customers
                    </td>
                  </tr>
                ) : (
                  topCustomers.map((c, i) => (
                    <tr
                      key={c._id || i}
                      className="border-t border-border hover:bg-muted transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm shrink-0">
                            {(c.name || c.email || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-foreground">
                              {c.name || 'No name'}
                            </p>
                            <p className="text-xs text-muted-foreground">{c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <SubBadge premium={c.premium_member === 1} ai={c.ai_plan === 1} />
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {c.expiry_date ? formatDate(c.expiry_date) : c.ai_plan_expiry ? formatDate(c.ai_plan_expiry) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(c.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Top Partners + Recent Signups ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Partners by Signups */}
          <div
            data-testid="table-top-partners"
            className="bg-card rounded-xl border border-border shadow-sm overflow-hidden"
          >
            <div className="p-5 border-b border-border flex items-center gap-2">
              <Handshake className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-base font-semibold">Top Partners by Signups</h2>
            </div>
            <div className="divide-y divide-border">
              {(partners.top_by_signups || []).length === 0 ? (
                <p className="px-5 py-8 text-sm text-muted-foreground text-center">
                  No partner data
                </p>
              ) : (
                (partners.top_by_signups || []).map((p, i) => (
                  <div key={p._id || i} className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-muted-foreground w-5">
                        #{i + 1}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {p.partner_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {p.company_name || '—'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-foreground">
                        {p.total_signups}
                      </p>
                      <p className="text-xs text-muted-foreground">signups</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent New Signups */}
          <div
            data-testid="table-recent-signups"
            className="bg-card rounded-xl border border-border shadow-sm overflow-hidden"
          >
            <div className="p-5 border-b border-border flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-base font-semibold">Recent New Signups (7d)</h2>
            </div>
            <div className="divide-y divide-border">
              {recentSignups.length === 0 ? (
                <p className="px-5 py-8 text-sm text-muted-foreground text-center">
                  No signups in last 7 days
                </p>
              ) : (
                recentSignups.slice(0, 8).map((c, i) => (
                  <div key={c._id || i} className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-semibold text-xs shrink-0">
                        {(c.name || c.email || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {c.name || 'No name'}
                        </p>
                        <p className="text-xs text-muted-foreground">{c.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <SubBadge premium={c.premium_member === 1} ai={c.ai_plan === 1} />
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatDate(c.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── Recent Payment History ── */}
        <div
          data-testid="table-recent-payments"
          className="bg-card rounded-xl border border-border shadow-sm overflow-hidden"
        >
          <div className="p-5 border-b border-border flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-base font-semibold">Recent Payment Events</h2>
            <span className="ml-auto text-xs text-muted-foreground">Last 10 events</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Customer
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Event
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Type
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentPayments.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      No payment events found
                    </td>
                  </tr>
                ) : (
                  recentPayments.map((p, i) => (
                    <tr
                      key={p._id || i}
                      className="border-t border-border hover:bg-muted transition-colors"
                    >
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium">
                          {p.customer_name || 'Unknown'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {p.customer_email || '—'}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 capitalize">
                          {(p.event_type || '—').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <SubBadge
                          premium={p.subscription_type === 'premium'}
                          ai={p.subscription_type === 'ai_plan'}
                        />
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {formatDateTime(p.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Partners Summary Footer ── */}
        <div
          data-testid="partners-summary"
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <div className="bg-card rounded-xl border border-border p-5 shadow-sm text-center">
            <p className="text-2xl font-bold text-foreground">
              {partners.total ?? '—'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Total Partners</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-5 shadow-sm text-center">
            <p className="text-2xl font-bold text-emerald-600">
              {partners.active ?? '—'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Active Partners</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-5 shadow-sm text-center">
            <p className="text-2xl font-bold text-foreground">
              {partners.totals?.total_signups ?? '—'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Total Partner Signups</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-5 shadow-sm text-center">
            <p className="text-2xl font-bold text-purple-600">
              {partners.new_this_month ?? '—'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">New Partners This Month</p>
          </div>
        </div>

        {/* ── Users by Country ── */}
        {countryData.length > 0 && (
          <div
            data-testid="chart-users-by-country"
            className="bg-card rounded-xl border border-border shadow-sm overflow-hidden"
          >
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <h2 className="text-base font-semibold">Users by Country</h2>
              </div>
              <span className="text-xs text-muted-foreground">{countryData.length} countries</span>
            </div>
            <div className="p-5">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={countryData}
                  layout="vertical"
                  margin={{ top: 0, right: 30, left: 10, bottom: 0 }}
                  barSize={18}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="country"
                    width={110}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    formatter={(value) => [value, 'Users']}
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                  />
                  <Bar dataKey="count" name="Users" radius={[0, 4, 4, 0]}>
                    {countryData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={[
                          '#6366f1','#8b5cf6','#06b6d4','#10b981','#f59e0b',
                          '#ef4444','#ec4899','#14b8a6','#f97316','#84cc16'
                        ][idx % 10]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

      </div>
    </CompanyAdminLayout>
  );
};

export default CompanyAdminDashboardPage;
